import { baseApi, PaginatedResponse } from 'sama-shared';
import type {
  CreateDeliveryPayoutAdminDto,
  CreateDeliveryPayoutBatchDto,
  DeliveryPayout,
  GetDeliveryPayoutFilterAdminDto,
  UpdateDeliveryPayoutAdminDto,
} from './types/payout';

export const deliveryPayoutApi = baseApi
  .enhanceEndpoints({ addTagTypes: ['DeliveryPayout', 'DeliverySettlement'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      getDeliveryPayouts: builder.query<
        PaginatedResponse<DeliveryPayout>,
        GetDeliveryPayoutFilterAdminDto
      >({
        query: (params) => ({
          url: '/admin/delivery-payout',
          method: 'GET',
          params: params || {},
        }),
        providesTags: (result) =>
          result?.data
            ? [
                ...result.data.map(({ id }) => ({ type: 'DeliveryPayout' as const, id })),
                { type: 'DeliveryPayout', id: 'LIST' },
              ]
            : [{ type: 'DeliveryPayout', id: 'LIST' }],
      }),

      getDeliveryPayoutById: builder.query<DeliveryPayout, string>({
        query: (id) => ({
          url: `/admin/delivery-payout/${id}`,
          method: 'GET',
        }),
        providesTags: (_result, _error, id) => [{ type: 'DeliveryPayout', id }],
      }),

      createDeliveryPayout: builder.mutation<DeliveryPayout, CreateDeliveryPayoutAdminDto>({
        query: (newPayout) => ({
          url: '/admin/delivery-payout',
          method: 'POST',
          body: newPayout,
        }),
        invalidatesTags: [
          { type: 'DeliveryPayout', id: 'LIST' },
          { type: 'DeliverySettlement', id: 'LIST' },
        ],
      }),

      createManualDeliveryPayoutBatch: builder.mutation<
        DeliveryPayout, // O la estructura de respuesta que retorne el backend (ej: DeliveryPayout[])
        CreateDeliveryPayoutBatchDto
      >({
        query: (batchDto) => ({
          url: '/admin/delivery-payout/manual/batch',
          method: 'POST',
          body: batchDto,
        }),
        invalidatesTags: [
          { type: 'DeliveryPayout', id: 'LIST' },
          { type: 'DeliverySettlement', id: 'LIST' },
        ],
      }),

      createAutomaticDeliveryPayoutBatch: builder.mutation<
        DeliveryPayout, // O la estructura de respuesta que retorne el backend (ej: DeliveryPayout[])
        CreateDeliveryPayoutBatchDto
      >({
        query: (batchDto) => ({
          url: '/admin/delivery-payout/automatic/batch',
          method: 'POST',
          body: batchDto,
        }),
        invalidatesTags: [
          { type: 'DeliveryPayout', id: 'LIST' },
          { type: 'DeliverySettlement', id: 'LIST' },
        ],
      }),

      updateDeliveryPayout: builder.mutation<
        DeliveryPayout,
        { id: string; body: UpdateDeliveryPayoutAdminDto }
      >({
        query: ({ id, body }) => ({
          url: `/admin/delivery-payout/${id}`,
          method: 'PATCH',
          body,
        }),
        invalidatesTags: (_result, _error, { id }) => [
          { type: 'DeliveryPayout', id },
          { type: 'DeliveryPayout', id: 'LIST' },
          { type: 'DeliverySettlement', id: 'LIST' },
        ],
      }),
    }),
    overrideExisting: false,
  });

export const {
  useGetDeliveryPayoutsQuery,
  useGetDeliveryPayoutByIdQuery,
  useCreateDeliveryPayoutMutation,
  useCreateManualDeliveryPayoutBatchMutation,
  useCreateAutomaticDeliveryPayoutBatchMutation,
  useUpdateDeliveryPayoutMutation,
} = deliveryPayoutApi;
